import {
  LAST_SUCCESS_TIME_LOG_IN,
  SLUG_LS
} from "@/const/local-storage.constants"
import { toLowerCase } from "@/helpers/string.helpers"
import { BaseAuthAdapter } from "./auth-adapter.base"
import {
  exchangeKodemSsoToken,
  signInWithKodemPassword
} from "./kodem-password"

import type { User } from "@/domain/user"
import type { Services } from "@/interfaces/services"
import type { FirebaseOptions } from "@firebase/app"

export type InitialParamsAuthAdapter = {
  slug?: string
} & FirebaseOptions

export class AuthAdapter
  extends BaseAuthAdapter
  implements Services.IAuthAdapter
{
  static defaultInstance: AuthAdapter

  protected currentUser: User

  protected company: string

  private subscribers: Record<string, Array<(data: any) => void>> = {}

  public AUTH_STATE_CHANGED_EVENT = "authStateChanged"

  public AUTH_MULTI_FACTOR_EVENT = "authMultiFactor"

  public AUTH_TRIAL_DAYS_EXPIRED = "authTrialDaysExpired"

  constructor({ slug }: { slug?: string }) {
    super()
    this.company = slug || ""
    this.currentUser = this.createUser({
      company: this.company,
      authorized: !!slug
    })
  }

  public trialDaysExpired = () => {}

  public syncUserWithAuthResponse = async (email?: string) => {
    try {
      this.onAuthStateChanged(email ? { email } : null)
    } catch (_) {
      this.signOut()
    }
  }

  public setUserExpired = () => {
    return
  }

  public resetUserExpired = () => {
    return
  }

  private onAuthStateChanged = async (user: { email: string } | null) => {
    this.setCurrentUser(user)
    this.publish(this.AUTH_STATE_CHANGED_EVENT, this.getCurrentUser())
  }

  private publish(eventName: string, data: unknown) {
    if (!Array.isArray(this.subscribers[eventName])) {
      return
    }

    this.subscribers[eventName].forEach((callback) => {
      callback(data)
    })
  }

  public setLastSuccessLoginTime = () => {
    localStorage.setItem(LAST_SUCCESS_TIME_LOG_IN, Date.now().toString())
  }

  public subscribe(eventName: string, callback: (data: any) => void) {
    if (!this.subscribers[eventName]) {
      this.subscribers[eventName] = []
    }

    this.subscribers[eventName].push(callback)

    return () => {
      this.subscribers[eventName] = this.subscribers[eventName].filter((cb) => {
        if (cb === callback) {
          return false
        }

        return true
      })
    }
  }

  public setCompany = (company: string) => {
    this.company = toLowerCase(company)
  }

  public setTenantId = (_?: string | null) => {}

  public setMultiFactorAuth = (_?: boolean) => {}

  // Self-hosted always checks passwords in the API; SSO follows the providers' auth backend
  public setKodemAuthBackend = (_?: boolean) => {}

  public getToken = () => Promise.resolve("")

  public getCurrentUser = () => this.currentUser

  public getCompany = () => this.company

  public getRecaptcha = () => null

  public signInWithToken = (_?: unknown) => {}

  // Exchanges the handoff token of a Kodem SSO login for the API session cookies
  public signInWithSsoToken = async (slug: string, ssoToken: string) => {
    const email = await exchangeKodemSsoToken(slug, ssoToken)

    this.setCompany(slug)
    this.setCurrentUser({ email })
    // Not announced: this runs in the SSO popup, which hands the sign-in over to the sign-in page
    // and closes. The app starting here would have its requests aborted by the close, and the
    // resulting sign-out clears the session the windows share
    this.onSuccessSignIn()

    return this.currentUser
  }

  // Self-hosted sessions, password and SSO alike, are renewed from the API refresh cookie
  public isKodemSession = () => false

  public signInWithSaml = (_?: unknown) => Promise.reject()

  public sendCodePhoneNumberEnrolling = (_?: unknown) => {}

  public verificationCode = (_: unknown, __: unknown) => {}

  public sendVerificationCodeOnPhoneNumber = () => null

  public signInWithGithub = () => Promise.reject()

  public signInWithGoogle = () => Promise.reject()

  public skipMultiFactorSetUp = () => {
    this.publish(this.AUTH_MULTI_FACTOR_EVENT, {
      userNeedVerifyPhoneNumberMultiFactor: false
    })
  }

  private onSuccessSignIn = () => {
    localStorage.setItem(SLUG_LS, this.company)
    this.setLastSuccessLoginTime()
  }

  private onSignIn: any = async () => {
    try {
      const user = this.getCurrentUser()

      if (user.authorized && this.company) this.onSuccessSignIn()

      this.onAuthStateChanged(user)

      return this.currentUser
    } catch {
      return this.currentUser
    }
  }

  signInWithEmailPass = async (email: string, password: string) => {
    const user = await signInWithKodemPassword(this.company, email, password)

    if (!user)
      return { error: true, translationKey: "invalid-login-credentials" }

    this.setCurrentUser(user)

    return this.onSignIn()
  }

  signOut = async () => {
    this.company = ""
    this.currentUser = this.createUser({ authorized: false })
    this.onAuthStateChanged(null)
  }

  isDemo = () => {
    return this.company === "kodemsecurity"
  }

  isInternalTenant = () => {
    return (
      this.company === "kodemsec" ||
      this.company === "kodemsec-qa" ||
      this.company === "sampledata" ||
      this.isDemo()
    )
  }
}
